import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "~/lib/utils";

const buttonVariants = cva(
  "focus-visible:ring-ring inline-flex w-fit cursor-pointer items-center justify-center gap-2 rounded-md text-sm font-bold tracking-[0.01em] whitespace-nowrap transition-colors focus-visible:ring-1 focus-visible:outline-none disabled:pointer-events-none disabled:cursor-default",
  {
    variants: {
      variant: {
        // Disabled colors per Figma's Button set: filled types use Button/Disabled fill +
        // Text/CTA/Disabled text, outlined types a Button/Disabled border, links
        // Button/Disabled text.
        primary: "bg-bouncie-500 text-primary-foreground disabled:bg-figma-button-disabled disabled:text-figma-text-cta-disabled shadow hover:bg-[#1156d4]",
        link: "text-primary disabled:text-figma-button-disabled underline-offset-2 hover:underline",
        "primary-alt": "text-bouncie-500 hover:bg-bouncie-500 disabled:bg-figma-button-disabled disabled:text-figma-text-cta-disabled bg-white hover:text-white",
        "secondary": "border-bouncie-500 text-bouncie-500 hover:bg-bouncie-500 disabled:border-figma-button-disabled disabled:text-figma-text-cta-disabled border-2 bg-transparent hover:text-white",
        "secondary-alt": "hover:text-bouncie-500 disabled:border-figma-button-disabled disabled:text-figma-text-cta-disabled border-2 border-white bg-transparent text-white hover:bg-white",
        tertiary: "bg-bouncie-100 text-bouncie-600 hover:bg-bouncie-200 hover:text-bouncie-700 disabled:bg-figma-button-disabled disabled:text-figma-text-cta-disabled",
      },
      size: {
        default: "h-[50px] px-6 py-4 text-sm",
        sm: "h-10 px-6 py-[10px] text-sm",
        lg: "h-14 px-6 py-4 text-base leading-5 tracking-[0.16px]",
        xl: "text-md h-[60px] min-h-[50px] min-w-[160px] px-6 py-[20px]",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, iconLeft, iconRight, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
        {iconLeft}
        <Slottable>{children}</Slottable>
        {iconRight}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
